/**
 * Diálogo para cambiar el plan contratado (equivalente al botón *Cambiar Plan*
 * del panel anterior: elegir plan + periodicidad con la que se renueva).
 *
 * El cambio está reservado al administrador principal: si otro usuario del
 * grupo `configuration` lo intenta, la API responde 403 y el aviso se muestra
 * dentro del propio diálogo.
 */

import { Crown } from 'lucide-react';
import { useState } from 'react';

import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';

import { precioPlan, textoLimite, textoModulos } from '../lib/empresa-form';
import type { PlanContratado, PlanDisponible } from '../empresa.types';

const BASE_TIPO =
  'inline-flex items-center rounded-control px-3 py-1.5 text-xs font-semibold transition-colors';
const TIPO_ACTIVO = 'bg-ink-900 text-white';
const TIPO_INACTIVO = 'border border-ink-200 bg-white text-ink-700 hover:border-brand-300';

interface Props {
  open: boolean;
  onClose: () => void;
  planes: PlanDisponible[];
  planActual: PlanContratado | null;
  guardando: boolean;
  error: string | null;
  onConfirmar: (planId: number, tipo: 'mensual' | 'anual') => void;
}

/** Diálogo de cambio de plan. */
export function EmpresaPlanDialogo({
  open,
  onClose,
  planes,
  planActual,
  guardando,
  error,
  onConfirmar,
}: Props) {
  const [seleccionado, setSeleccionado] = useState<number | null>(planActual?.id ?? null);
  const [tipo, setTipo] = useState<'mensual' | 'anual'>(planActual?.tipo ?? 'mensual');

  const elegido = planes.find((plan) => plan.id === seleccionado) ?? null;
  const mismoPlan = elegido !== null && elegido.id === planActual?.id && tipo === planActual?.tipo;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="lg"
      title="Cambiar plan"
      description="La suscripción se renueva desde hoy con la periodicidad elegida."
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose} disabled={guardando}>
            Cancelar
          </Button>
          <Button
            size="sm"
            loading={guardando}
            disabled={!elegido || mismoPlan}
            onClick={() => elegido && onConfirmar(elegido.id, tipo)}
          >
            Contratar plan
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? <Alert variant="error" title="No se pudo cambiar el plan">{error}</Alert> : null}

        {!planActual ? (
          <Alert variant="info">
            La empresa no tiene un plan asignado: al contratar uno se activa desde hoy.
          </Alert>
        ) : null}

        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-ink-400">
            Periodicidad
          </span>
          {(['mensual', 'anual'] as const).map((opcion) => (
            <button
              key={opcion}
              type="button"
              className={`${BASE_TIPO} ${tipo === opcion ? TIPO_ACTIVO : TIPO_INACTIVO}`}
              aria-pressed={tipo === opcion}
              onClick={() => setTipo(opcion)}
            >
              {opcion === 'anual' ? 'Anual' : 'Mensual'}
            </button>
          ))}
        </div>

        <ul className="flex flex-col gap-2">
          {planes.map((plan) => {
            const activo = plan.id === seleccionado;
            const actual = plan.id === planActual?.id;

            return (
              <li key={plan.id}>
                <button
                  type="button"
                  aria-pressed={activo}
                  onClick={() => setSeleccionado(plan.id)}
                  className={`w-full rounded-control border p-4 text-left transition-colors ${
                    activo
                      ? 'border-brand-400 bg-brand-50/60 ring-4 ring-brand-100'
                      : 'border-ink-200 bg-white hover:border-brand-300'
                  }`}
                >
                  <span className="flex flex-wrap items-center gap-2">
                    <span
                      aria-hidden="true"
                      className="size-2.5 rounded-full"
                      style={{ backgroundColor: plan.color }}
                    />
                    <span className="text-sm font-semibold text-ink-900">{plan.nombre}</span>
                    <Badge variant="outline">{plan.codigo}</Badge>
                    {actual ? (
                      <Badge variant="brand">
                        <Crown className="mr-1 size-3" aria-hidden="true" /> Plan actual
                      </Badge>
                    ) : null}
                    <span className="ml-auto text-sm font-semibold text-ink-900">
                      {precioPlan(plan, tipo)}
                      <span className="ml-1 text-xs font-normal text-ink-500">
                        /{tipo === 'anual' ? 'año' : 'mes'}
                      </span>
                    </span>
                  </span>

                  {plan.descripcion ? (
                    <span className="mt-1 block text-xs text-ink-500">{plan.descripcion}</span>
                  ) : null}

                  <span className="mt-2 block text-xs text-ink-600">
                    {textoModulos(plan.total_modulos)} · hasta {textoLimite(plan.max_usuarios)}{' '}
                    usuarios y {textoLimite(plan.max_terminales)} terminales
                  </span>
                </button>
              </li>
            );
          })}
        </ul>

        <p className="text-xs text-ink-500">
          Los módulos de cada plan se configuran en <strong>Gestión de planes</strong>.
        </p>
      </div>
    </Dialog>
  );
}
